import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import FontFaceObserver from "fontfaceobserver";
import Moment from "react-moment";
import "moment-timezone";
import { getEventBySlug } from "../../../utils/api/helpers/events";
import fysee25LogoWhite from "../../../assets/img/fysee/FYSEE_26_LA_logo.png";
import iconCalendar from "../../../assets/img/fysee/icon-calendar.svg";
import iconClock from "../../../assets/img/fysee/icon-clock.svg";
import iconPin from "../../../assets/img/fysee/icon-pin.svg";
import EventRSVPForm from "./EventRSVPForm";

export default function EventDetail() {
  const { eventSlug } = useParams();
  const [fontLoaded, setFontLoaded] = useState(false);
  const [eventLoaded, setEventLoaded] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [eventTitle, setEventTitle] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [eventShows, setEventShows] = useState([]);
  const [allowGuests, setAllowGuests] = useState(false);
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [eventMediaSrc, setEventMediaSrc] = useState(null);
  const [venueTimezone, setVenueTimezone] = useState(null);
  const [venueName, setVenueName] = useState(null);
  const [venueMapUrl, setVenueMapUrl] = useState(null);
  const [guestThreshold, setGuestThreshold] = useState(0);
  const [totalThreshold, setTotalThreshold] = useState(0);
  const [totalRSVPCount, setTotalRSVPCount] = useState(0);

  const s3Url = "https://jwlfyc-public-us-east-1.s3.amazonaws.com/";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let mounted = true;
    const font = new FontFaceObserver("NetflixSansCond");

    font
      .load()
      .then(() => {
        if (mounted) {
          setFontLoaded(true);
        }
      })
      .catch(() => {
        console.error("Font failed to load");
        if (mounted) {
          setFontLoaded(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let subscribed = true;
    setEventLoaded(false);

    Moment.globalTimezone = "America/Los_Angeles";
    Moment.globalLocale = "en-us";

    const loadEvent = async () => {
      try {
        const result = await getEventBySlug(eventSlug);

        if (!subscribed || !result?.data) {
          return;
        }

        // console.log("getEventBySlug", result.data);
        const shows = Array.isArray(result.data.shows) ? result.data.shows : [];
        const hasSingleShow = shows.length === 1;

        setEventTitle(result.data.title || "");
        setEventDescription(result.data.description || "");
        setAllowGuests(result.data.allow_guests > 0);
        setEmailDisabled(result.data.email_disabled);
        setGuestThreshold(result.data.guest_threshold || 0);
        setTotalThreshold(
          (result.data.rsvp_threshold ?? 0) +
            (result.data.confirmed_threshold ?? 0) +
            (result.data.waitlist_threshold ?? 0) -
            (result.data.reserved_seats ?? 0),
        );
        setTotalRSVPCount(result.data.total_rsvps || 0);
        setVenueName(result.data.venue_name || null);
        setVenueTimezone(result.data.venue_timezone || null);
        setEventShows(shows);

        if (
          result.data.venue_name &&
          result.data.venue_address &&
          result.data.venue_city &&
          result.data.venue_state &&
          result.data.venue_zipcode
        ) {
          setVenueMapUrl(
            [
              result.data.venue_name,
              result.data.venue_address,
              result.data.venue_city,
              result.data.venue_state,
              result.data.venue_zipcode,
            ]
              .join(" ")
              .trim()
              .split(/\s+/)
              .join("+"),
          );
        } else {
          setVenueMapUrl(null);
        }

        setEventMediaSrc(
          hasSingleShow && result.data.filename
            ? s3Url + result.data.filename
            : fysee25LogoWhite,
        );

        if (result.data.start_date) {
          const tempDateTime =
            result.data.start_date.split("T")[0] + "T12:00:00.000";
          setStartDate(tempDateTime);
        } else {
          setStartDate(null);
        }

        if (result.data.start_time) {
          const timeParts = result.data.start_time.split(":");
          const hours = `${timeParts[0]}:${timeParts[1]}`;
          const hoursFormatted = tConvert(hours);
          setStartTime(hoursFormatted);
        } else {
          setStartTime(null);
        }

        setEventLoaded(true);
      } catch (error) {
        console.error("getEventBySlug failed", error);
        if (subscribed) {
          setEventLoaded(true);
        }
      }
    };

    loadEvent();

    return () => {
      subscribed = false;
    };
  }, [eventSlug, s3Url]);

  const tConvert = (time) => {
    time = time
      .toString()
      .match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];

    if (time.length > 1) {
      time = time.slice(1);
      time[5] = +time[0] < 12 ? " AM" : " PM";
      time[0] = +time[0] % 12 || 12;
    }

    return time.join("");
  };

  const hasMultipleShows = useMemo(() => {
    return Array.isArray(eventShows) && eventShows.length > 1;
  }, [eventShows]);

  const hasSingleShow = useMemo(() => {
    return Array.isArray(eventShows) && eventShows.length === 1;
  }, [eventShows]);

  const rsvpClosed = useMemo(() => {
    if (!eventLoaded) {
      return false;
    }

    return emailDisabled === 1 || totalRSVPCount >= totalThreshold;
  }, [eventLoaded, emailDisabled, totalRSVPCount, totalThreshold]);

  return (
    <>
      <div className="events-page-container">
        <Helmet>
          <title>{`${eventTitle} | Netflix Series FYC`}</title>
          <meta name="description" content={`Events | Netflix Series FYC`} />
        </Helmet>

        <div
          className="events-page-content"
          style={{ opacity: fontLoaded ? 1 : 0 }}
          aria-busy={!fontLoaded || !eventLoaded}
        >
          <div className="width-ctrl">
            <div className={"events-detail-page-container nf-sans-cond"}>
              <header className={"events-page-header"}>
                <div className={"events-page-header-title"}>
                  <h1>
                    <span className={"fysee-gradient-text"}>{eventTitle}</span>
                  </h1>
                </div>

                <div className={"events-page-header-right"}>
                  <div
                    className={"events-page-header-img"}
                    style={{
                      padding: hasSingleShow ? "0px" : "8px",
                    }}
                  >
                    {eventMediaSrc ? (
                      <img
                        src={eventMediaSrc}
                        alt={eventTitle ? `${eventTitle} event artwork` : ""}
                        className={""}
                      />
                    ) : null}
                  </div>

                  {hasMultipleShows ? (
                    <div
                      className={"events-page-header-shows"}
                      aria-label="Featured shows"
                    >
                      {eventShows.map((o, i) => {
                        return (
                          <span key={o?.title || i} className="">
                            {i > 0 ? ", " : ""} {o.title}
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </header>

              <div className={"events-page-content events-detail-page-content"}>
                <div
                  className={"col events-page-content-copy nf-sans"}
                  dangerouslySetInnerHTML={{ __html: eventDescription }}
                />

                <div className={"col events-page-content-details"}>
                  {startDate ? (
                    <div
                      className={"events-page-content-meta"}
                      aria-label="Event details"
                    >
                      <div className={"events-page-content-meta-row"}>
                        <div
                          className={
                            "d-flex align-items-center events-page-content-meta-icon"
                          }
                          aria-hidden="true"
                        >
                          <img src={iconCalendar} className={""} alt="" />
                        </div>
                        <div className={"events-page-meta-value"}>
                          <Moment format="dddd, MMM DD">{startDate}</Moment>
                        </div>
                      </div>

                      <div className={"events-page-content-meta-row"}>
                        <div
                          className={
                            "d-flex align-items-center events-page-content-meta-icon"
                          }
                          aria-hidden="true"
                        >
                          <img src={iconClock} className={""} alt="" />
                        </div>
                        <div className={"events-page-meta-value"}>
                          {startTime} {venueTimezone}
                        </div>
                      </div>

                      <div className={"events-page-content-meta-row"}>
                        <div
                          className={
                            "d-flex align-items-center events-page-content-meta-icon"
                          }
                          aria-hidden="true"
                        >
                          <img src={iconPin} className={""} alt="" />
                        </div>
                        <div className={"events-page-meta-value"}>
                          {venueName}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className={"col events-page-content-map"}>
                  {venueMapUrl ? (
                    <iframe
                      title={
                        venueName ? `Map of ${venueName}` : "Event venue map"
                      }
                      src={`https://www.google.com/maps?q=${venueMapUrl}&output=embed`}
                      width="100%"
                      height="350"
                      style={{ display: "block", border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  ) : null}
                </div>
              </div>
            </div>

            <div className={"events-page-rsvp-form-container"}>
              <div className={"events-page-rsvp-header"}>
                <h2>
                  <span className={"fysee-gradient-text"}>RSVP</span>
                </h2>
              </div>
              <div className={"events-page-rsvp-content"}>
                {!eventLoaded ? null : rsvpClosed ? (
                  <div
                    className="text-center text-uppercase fw-bold my-3"
                    style={{ fontSize: "1.5rem" }}
                    role="status"
                    aria-live="polite"
                  >
                    RSVPs to this event are closed.
                  </div>
                ) : (
                  <EventRSVPForm
                    allowGuests={allowGuests}
                    event_slug={eventSlug}
                    guestThreshold={guestThreshold}
                    start_date={startDate}
                    start_time={startTime}
                    title={eventTitle}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="position-fixed z-0 w-100 h-100"
        style={{ top: 0, left: 0, background: "#1a1a1a" }}
      />
      {/*<div className="events-page-background" />*/}
      {/*<div className="events-page-background-gradient" />*/}
    </>
  );
}
